import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IProductDataV3 } from '@df/catalog/product/product';
import { ImageModule } from '@df/module-image/image.module';
import { Basket } from '@df/session/api/basket/basket';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { Nexus } from '@tomandco/nexus-api-types';
import { Product } from '../product.decorator';

interface IProductInfoDeliveryBlockDataContent {
    maxWeeksDelivery: number;
    text: string;
    ctaText?: string;
    ctaUrl?: string;
    image: string;
}

@Component({
    selector: 'product-info-delivery',
    templateUrl: './product-info-delivery.html',
    standalone: true,
    imports: [CommonModule, RouterLink, ImageModule, ICmsClientModule]
})
@ICmsComponent<ProductInfoDeliveryComponent>({
    id: 'cms-product-info-delivery',
    block: {
        url: 'cms-product-info-delivery'
    },
    onDemandOnly: true,
    name: 'Product Info Delivery',
    fields: [
        { key: 'maxWeeksDelivery', type: NICmsContentField.EFieldType.number, title: 'Maximum weeks delivery', required: true },
        { key: 'text', type: NICmsContentField.EFieldType.text, title: 'Text', required: true },
        { key: 'ctaText', type: NICmsContentField.EFieldType.text, title: 'CTA text' },
        { key: 'ctaUrl', type: NICmsContentField.EFieldType.text, title: 'CTA Url' },
        { key: 'image', type: NICmsContentField.EFieldType.image, title: 'Image', required: true }
    ]
})
export class ProductInfoDeliveryComponent extends UiComponent implements NICmsContentComponent.IComponent, OnChanges {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: IProductInfoDeliveryBlockDataContent;

    @Input()
    product?: Product<IProductDataV3>;

    @Input()
    buyRequest?: Omit<Nexus.Lib.Basket.BasketRequestData, 'id'>

    @Input()
    qty?: number;

    basketMode = false;

    basket = inject(Basket);

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.add(this.basket.subscribe(() => this.changeDetectorRef.detectChanges()));
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.subscriptions.unsubscribe();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        if (changes['product'] && this.initiated) {
            this.detectChanges();
        }
    }

    get isInStock(): boolean {
        if (this.qty && this.product?.isSalable(this.qty, this.buyRequest, !this.basketMode)) {
            const qty = Math.max(this.product.getStockQty(false, this.buyRequest, !this.basketMode), 0);
            return this.qty <= qty;
        }
        return false;
    }

    private get leadtime(): number | undefined {
        const qty = Math.max(this.product?.getStockQty(false, this.buyRequest, !this.basketMode) || 0, 0);
        if (qty < (this.qty || 0)) {
            const backordersQty = (this.qty || 0) - qty;
            const firstPoQty = this.firstPoQty && this.firstPoTimeInWeeks ? Math.min(this.firstPoQty, backordersQty) : 0;
            if (backordersQty <= firstPoQty) {
                return this.firstPoTimeInWeeks;
            } else {
                return this.leadtimeInWeeks;
            }
        }
        return this.inStockLeadtimeInWeeks;
    }

    private get inStockLeadtimeInWeeks(): number {
        return 1;
    }

    get display(): boolean {
        if (this.leadtime && this.content?.maxWeeksDelivery) {
            return this.leadtime <= this.content?.maxWeeksDelivery || this.client.isCms;
        }
        return false;
    }

    private get leadtimeInWeeks(): number | undefined {
        const leadtime = this.product?.data?.leadtime;
        if (leadtime) {
            return this.getWeeksFromDays(leadtime);
        }
        return undefined;
    }

    get firstPoQty(): number {
        return this.product?.data?.first_po_quantity || 0;
    }

    get firstPoTimeInWeeks(): number | undefined {
        const eta = this.product?.data?.po_in_stock_eta;
        if (eta) {
            const etaDate = new Date(eta);
            const etaTime = etaDate && etaDate.getTime();
            if (etaTime) {
                return this.getWeeksFromDays((etaTime - Date.now()) / (1000 * 60 * 60 * 24));
            }
        }
        return undefined;
    }

    private getWeeksFromDays(days: number): number {
        return Math.ceil(days / 7);
    }
}
